import * as ko from "knockout";
import * as toastr from "toastr";
import { BaseService } from "../_common/_baseService";
import { portalHttpService } from "../_common/httpService";
import { MeterReadingSearchCriteria, MeterReadingValue, searchResult, uploadMeterReading } from "organized";
import { AttachmentResultDTO, downloadAttachmentFromBase64 } from "../attachment.service";
import labels from "app/labels";
import { ISubmitMeterHeader } from "components/contracts/submit-meters/submit-meter-header";
import * as moment from "moment";
import { DISPLAY_DATETIME_DEFAULT_FORMAT } from "app/custom-observables";
import { USER_LANGUAGE_KEY } from "app/app-constants";

export const METTER_SESSION_STORAGE_KEY = "_simplified_meters_session_id";

class SimplifiedMetersService extends BaseService {
  constructor() {
    super("simplifiedMeters");
  }

  public sendCode = async (email: string): Promise<void> => {
    const locale = localStorage.getItem(USER_LANGUAGE_KEY);
    await portalHttpService.post<void>(`${this.baseApiUrl()}/SendCode`, ko.toJSON({ email, locale }));
  };

  public verifyCode = async (sessionId: string, code: string): Promise<string> => {
    return await portalHttpService.post<string>(`${this.baseApiUrl()}/VerifyCode`, ko.toJSON({ sessionId, code }));
  };

  public searchMeterReadings = async (criteria: MeterReadingSearchCriteria, sessionId: string, isReadonly: boolean) => {
    const result = await portalHttpService.post<{ result: any[]; count: number }>(
      `${this.baseApiUrl()}/SearchMeterReadings`,
      ko.toJSON({ criteria, sessionId, isReadonly }),
    );

    return new searchResult<MeterReadingValue[]>({
      count: result.count,
      result: result?.result?.map((meter) => new MeterReadingValue(meter)) || [],
    });
  };

  public saveMeterReadings = async (meter_readings: MeterReadingValue[], readingState: string, sessionId: string) => {
    const filtered = meter_readings.filter((item) => {
      return item.meter_value_new();
    });

    const data = ko.toJSON({
      meter_readings: filtered,
      reading_state: readingState,
      session_id: sessionId,
    });
    await portalHttpService.post<any>(`${this.baseApiUrl()}/SaveMeterReadings`, data);
  };

  public exportMeterReadingsFile = async (
    offset: number,
    headerLabels: string[],
    sessionId: string,
    criteria: MeterReadingSearchCriteria,
    isReadonly: boolean,
  ) => {
    const locale = localStorage.getItem(USER_LANGUAGE_KEY);
    let labelsRequested = [labels.descr("cSequence")];
    labelsRequested = [...labelsRequested, ...headerLabels.map((label) => labels.descr(label))];
    const fileResult: AttachmentResultDTO = await portalHttpService.post(
      `${this.baseApiUrl()}/exportContractMeterReadingsFile`,
      ko.toJSON({
        criteria: {
          ...criteria,
          isExport: true,
        },
        sessionId,
        parameters: {
          offset,
          labels: labelsRequested,
          locale,
        },
      }),
    );
    downloadAttachmentFromBase64(fileResult);
  };

  public uploadBulkMeters = async (attachment, sessionId: string): Promise<uploadMeterReading[]> => {
    const locale = localStorage.getItem(USER_LANGUAGE_KEY);

    var formData = new FormData();
    formData.append("imageData", dataURLtoBlob(attachment.file.filedata()));
    formData.append("attachment_name", attachment.attachment_name);
    formData.append("attachment_type", attachment.attachment_type);
    formData.append("file_type", attachment.file_type);
    formData.append("attachment_description", attachment.attachment_description);
    formData.append("customer_status", attachment.customer_status);
    formData.append("file_has_header", attachment.file_has_header);
    formData.append("test_meters_file", attachment.test_meters_file);
    formData.append("contract_id", attachment.contract_id);
    formData.append("session_id", sessionId);
    formData.append("locale", locale);
    toastr.success(labels.descr("cc_P_UPLOAD_STARTED"));
    const data = await portalHttpService.ajaxPost<any>(`${this.baseApiUrl()}/uploadBulkMeters/`, formData);

    if (!attachment.test_meters_file) {
      toastr.success(labels.descr("cc_P_METER_READINGS_UPDATED"));
    }

    if (data.display_preview) {
      if (data.excelWithErrors) {
        const newdata =
          "data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64," +
          escape(data.excelWithErrors);
        const a = $("<a />");
        a.attr("download", data.file_name);
        a.attr("href", newdata);
        $("body").append(a);
        a[0].click();
        $(a).remove();
      }

      return data.file_data
        ?.map((item) => {
          if (item.reading_date?.value) {
            item.reading_date.value = moment(item.reading_date.value).format(DISPLAY_DATETIME_DEFAULT_FORMAT + " A");
          }

          return item;
        })
        .map((item) => new uploadMeterReading(item));
    }

    return null;
  };
}

function dataURLtoBlob(dataurl) {
  var arr = dataurl.split(","),
    mime = arr[0].match(/:(.*?);/)[1],
    bstr = atob(arr[1]),
    n = bstr.length,
    u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
}

export const simplifiedMetersService = new SimplifiedMetersService();
